import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import type { AnalyticsBreakdown, AnalyticsSummary, Opportunity } from '../../api/types';
import { Bar, Donut, Histogram, HorizontalBar, LineArea, Pie, Sparkline, type HBarDatum, type VolumeChartType } from '../../charts';
import { OPEN_CHAT_EVENT } from '../chatbot';
import {
  Button,
  ChartCard,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  KpiTile,
  LiveBadge,
  Modal,
  Select,
  Skeleton,
  useToast,
  type FilterOption,
  type KpiDelta,
  type SelectOption,
} from '../../components';
import { CATEGORICAL_TOKENS, categoryColor } from '../../lib/category';
import { UPLOAD_PATH } from '../documents/uploadPath';

import './dashboard.css';
import {
  drillToDocuments,
  hasActiveFilters,
  parseFilters,
  serializeFilters,
  withDimension,
  type DashboardFilters,
  type DimensionKey,
} from './filters';
import {
  bandStyle,
  confidenceBands,
  countDelta,
  fmtBig,
  oldestAgeLabel,
  periodLabel,
  periodLongLabel,
  pointDelta,
} from './format';
import { useDashboard, type DashboardData } from './useDashboard';

const UNKNOWN_KEY = '__unknown__';

/** True when a dimension exists but every bucket is the unmapped placeholder. */
function isUnmapped(breakdown: AnalyticsBreakdown): boolean {
  return breakdown.buckets.length > 0 && breakdown.buckets.every((b) => b.key === UNKNOWN_KEY);
}

/** Filter-chip options for a dimension, dropping the unmapped placeholder. */
function optionsFor(breakdown: AnalyticsBreakdown): FilterOption[] {
  return breakdown.buckets
    .filter((b) => b.key !== UNKNOWN_KEY)
    .map((b) => ({ value: b.key, label: b.label, count: b.documents }));
}

export function DashboardScreen(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const state = useDashboard(filters);

  const setFilters = useCallback(
    (next: DashboardFilters) => {
      setSearchParams(serializeFilters(next));
    },
    [setSearchParams],
  );

  const setDimension = useCallback(
    (key: DimensionKey, value: string | undefined) => {
      setFilters(withDimension(filters, key, value));
    },
    [filters, setFilters],
  );

  const clearAll = useCallback(() => {
    setFilters({ start: filters.start, end: filters.end });
  }, [filters.start, filters.end, setFilters]);

  // `navigate` returns a promise in react-router 7; wrap it so handlers stay
  // void-returning (no floating promises, no promise-to-void-attr mismatch).
  const go = useCallback(
    (to: string) => {
      void navigate(to);
    },
    [navigate],
  );

  const drill = useCallback(
    (key: DimensionKey, value: string) => {
      void navigate(drillToDocuments(filters, key, value));
    },
    [filters, navigate],
  );

  const askAbout = useCallback(
    (prompt: string) => {
      window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: { message: prompt } }));
      toast('Opening Ask Docsight…');
    },
    [toast],
  );

  const saveView = useCallback(() => {
    const link = window.location.href;
    // Clipboard is best-effort (the URL is already the shareable view) and is not
    // present in every environment, so treat it as optional and swallow a reject.
    const nav = navigator as { clipboard?: Clipboard };
    if (nav.clipboard) {
      void nav.clipboard.writeText(link).catch(() => undefined);
    }
    toast('View saved — link copied');
  }, [toast]);

  if (state.status === 'loading') return <DashboardLoading />;
  if (state.status === 'error') {
    return (
      <section className="ds-dash" aria-label="Dashboard">
        <ErrorState
          title="We couldn't load your analytics"
          body="The analytics service didn't respond. Your documents are safe — this only affects the rollup view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { data } = state;
  // Only an unfiltered, all-time zero means "nothing ingested yet"; zero inside a
  // chosen date range is just a quiet period and gets the normal empty charts.
  const firstRun =
    !hasActiveFilters(filters) &&
    filters.start === undefined &&
    data.summary.current.documents === 0;
  if (firstRun) {
    return (
      <section className="ds-dash" aria-label="Dashboard">
        <FirstRunEmpty
          processing={state.processing}
          onConnect={() => {
            go('/settings/sources');
          }}
          onUpload={() => {
            go(UPLOAD_PATH);
          }}
        />
      </section>
    );
  }

  return (
    <section className="ds-dash" aria-label="Dashboard">
      <FilterBar
        onClearAll={hasActiveFilters(filters) ? clearAll : undefined}
        context={
          <span className="ds-dash__context">
            {/* The in-flight note comes first: it explains why a number may move
                on its own, and why it may not match Documents for a moment. */}
            {state.processing > 0 ? (
              <>
                <LiveBadge count={state.processing} />
                {' · '}
              </>
            ) : null}
            {filters.start !== undefined ? 'vs previous period · ' : 'All time · '}
            <button type="button" className="ds-linkbtn" onClick={saveView}>
              Save view
            </button>
          </span>
        }
      >
        <FilterChip
          name="Type"
          options={optionsFor(data.facets.type)}
          selected={filters.type !== undefined ? [filters.type] : []}
          onChange={(sel) => {
            setDimension('type', sel[0]);
          }}
        />
        <FilterChip
          name="Location"
          options={optionsFor(data.facets.location)}
          selected={filters.location !== undefined ? [filters.location] : []}
          onChange={(sel) => {
            setDimension('location', sel[0]);
          }}
        />
        <FilterChip
          name="Scanner"
          options={optionsFor(data.facets.scanner)}
          selected={filters.scanner !== undefined ? [filters.scanner] : []}
          onChange={(sel) => {
            setDimension('scanner', sel[0]);
          }}
        />
        <FilterChip
          name="Source"
          options={optionsFor(data.facets.source)}
          selected={filters.source !== undefined ? [filters.source] : []}
          onChange={(sel) => {
            setDimension('source', sel[0]);
          }}
        />
      </FilterBar>

      <KpiRow summary={data.summary} timeseries={data.timeseries.points} />

      {data.review.count > 0 ? (
        <ReviewBacklogBanner
          count={data.review.count}
          oldestCreatedAt={data.review.oldestCreatedAt}
          onOpen={() => {
            go('/review');
          }}
        />
      ) : null}

      {isUnmapped(data.breakdowns.location) || isUnmapped(data.breakdowns.scanner) ? (
        <PartialBanner
          onName={() => {
            go('/settings/locations');
          }}
        />
      ) : null}

      <div className="ds-dash__row-primary">
        <VolumeCard
          points={data.timeseries.points}
          onAsk={() => {
            askAbout('How has document volume trended over this period?');
          }}
          onExport={() => {
            toast('Preparing volume export…');
          }}
        />
        <TypeMixCard
          breakdown={data.breakdowns.type}
          onDrill={(key) => {
            drill('type', key);
          }}
          onViewAll={() => {
            drill('type', '');
          }}
          onAsk={() => {
            askAbout('What is the document type mix?');
          }}
        />
      </div>

      <div className="ds-dash__row-breakdown">
        <DimensionBarCard
          title="By location"
          dimension="location"
          breakdown={data.breakdowns.location}
          onDrill={(key) => {
            drill('location', key);
          }}
          onAsk={() => {
            askAbout('How do documents break down by location?');
          }}
        />
        <DimensionBarCard
          title="By scanner"
          dimension="scanner"
          breakdown={data.breakdowns.scanner}
          onDrill={(key) => {
            drill('scanner', key);
          }}
          onAsk={() => {
            askAbout('How do documents break down by scanner?');
          }}
        />
        <SourceDonutCard
          breakdown={data.breakdowns.source}
          onDrill={(key) => {
            drill('source', key);
          }}
          onAsk={() => {
            askAbout('How do documents break down by source?');
          }}
        />
        <ConfidenceCard
          breakdown={data.breakdowns.confidence}
          onAsk={() => {
            askAbout('What does the classification confidence distribution look like?');
          }}
        />
      </div>

      <Opportunities
        items={data.opportunities}
        onViewAll={() => {
          go('/automation');
        }}
        onOpen={() => {
          go('/automation');
        }}
      />
    </section>
  );
}

// --------------------------------------------------------------------- states

function DashboardLoading(): React.JSX.Element {
  return (
    <section className="ds-dash" aria-label="Dashboard" aria-busy="true">
      <div className="ds-dash__kpis">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="ds-kpi">
            <Skeleton width="70%" height={11} radius={5} />
            <div style={{ marginTop: 10 }}>
              <Skeleton width="50%" height={22} radius={5} />
            </div>
          </div>
        ))}
      </div>
      <div className="ds-dash__skel-charts">
        <ChartCard title="Volume over time" loading loadingHeight={210} />
        <ChartCard title="Document type mix" loading loadingHeight={210} />
      </div>
      <p className="ds-dash__loading-caption">Loading analytics from rollups…</p>
    </section>
  );
}

function DocumentsIcon(): React.JSX.Element {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 2h8l4 4v16H6zM14 2v4h4"
        stroke="var(--a-ink)"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The state a brand-new tenant lands on. It offers two honest ways forward: wire
 * up a scanner for real, or upload one file by hand and watch the same pipeline
 * run — which is how you prove the stack end to end before any hardware exists.
 *
 * `processing > 0` is its own case: documents HAVE been ingested, they are just
 * not in the rollups yet (the events worker folds a document in once it is
 * classified). Saying "no documents yet" there would be plainly wrong, so the
 * copy switches to the truth and the view polls itself until the charts fill in.
 */
function FirstRunEmpty({
  processing,
  onConnect,
  onUpload,
}: {
  processing: number;
  onConnect: () => void;
  onUpload: () => void;
}): React.JSX.Element {
  const inFlight = processing > 0;
  return (
    <EmptyState
      icon={<DocumentsIcon />}
      title={inFlight ? 'Your first documents are processing' : 'No documents yet'}
      body={
        inFlight
          ? "They're moving through OCR and classification now. This dashboard fills in by itself as each one lands — no need to refresh."
          : "Connect a source and your scanned documents will start appearing here — OCR'd, classified, and ready to explore. Or upload one now to watch the pipeline run."
      }
      actions={
        inFlight ? (
          <LiveBadge count={processing} />
        ) : (
          <>
            <Button variant="primary" onClick={onUpload}>
              Upload a document
            </Button>
            <Button variant="secondary" onClick={onConnect}>
              Create an API key
            </Button>
          </>
        )
      }
    >
      <ol
        style={{
          display: 'flex',
          gap: 28,
          marginTop: 36,
          textAlign: 'left',
          listStyle: 'none',
          padding: 0,
          flexWrap: 'wrap',
        }}
      >
        {[
          ['Upload or connect', 'by hand, or with a key'],
          ['It reaches the queue', 'POST /v1/documents'],
          ['Watch it flow', 'ingest → OCR → classify'],
        ].map(([bold, rest], i) => (
          <li key={bold} style={{ maxWidth: 190, display: 'flex', gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--hov)',
                color: 'var(--t2)',
                fontSize: 11,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              {i + 1}
            </span>
            <span style={{ fontSize: 12, color: 'var(--t2)' }}>
              <strong style={{ color: 'var(--t1)' }}>{bold}</strong>
              <br />
              {rest}
            </span>
          </li>
        ))}
      </ol>
    </EmptyState>
  );
}

function ReviewBacklogBanner({
  count,
  oldestCreatedAt,
  onOpen,
}: {
  count: number;
  oldestCreatedAt: string | null;
  onOpen: () => void;
}): React.JSX.Element {
  return (
    <div className="ds-banner ds-banner--warn" role="status">
      <span className="ds-banner__dot" aria-hidden="true" />
      <span>
        <strong>
          {count.toLocaleString('en-GB')} document{count === 1 ? '' : 's'}
        </strong>{' '}
        {count === 1 ? 'is' : 'are'} waiting for review
        {oldestCreatedAt !== null ? (
          <>
            {' '}
            — oldest has been queued <strong>{oldestAgeLabel(oldestCreatedAt)}</strong>
          </>
        ) : null}
        .
      </span>
      <span className="ds-banner__spacer" />
      <Button size="sm" variant="primary" onClick={onOpen}>
        Open review queue
      </Button>
    </div>
  );
}

function PartialBanner({ onName }: { onName: () => void }): React.JSX.Element {
  return (
    <div className="ds-banner ds-banner--info" role="status">
      <span>
        Some scanners and locations aren't named yet, so those breakdowns show as{' '}
        <strong>Unknown</strong>. Map your devices to break analytics down by site and scanner.
      </span>
      <span className="ds-banner__spacer" />
      <button type="button" className="ds-banner__link" onClick={onName}>
        Name them in Settings
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------- KPIs

interface TileSpec {
  label: string;
  value: string;
  /** Absent for an all-time window, which has no previous period to compare. */
  delta?: KpiDelta;
  inverted: boolean;
  series: number[];
}

function buildTiles(
  summary: AnalyticsSummary,
  docs: number[],
  pages: number[],
): TileSpec[] {
  const { current: c, previous: p, deltas: d } = summary;
  const compare = p !== null && d !== null;
  return [
    {
      label: 'Total documents',
      value: fmtBig(c.documents),
      delta: compare ? countDelta(c.documents, p.documents) : undefined,
      inverted: false,
      series: docs,
    },
    {
      label: 'Pages processed',
      value: fmtBig(c.pages),
      delta: compare ? countDelta(c.pages, p.pages) : undefined,
      inverted: false,
      series: pages,
    },
    {
      label: 'Auto-classified',
      value: `${c.auto_classified_pct.toFixed(1)}%`,
      delta: compare ? pointDelta(d.auto_classified_pct, { goodWhenUp: true, unit: 'pt' }) : undefined,
      inverted: false,
      series: docs,
    },
    {
      label: 'Needs review',
      value: `${c.needs_review_pct.toFixed(1)}%`,
      delta: compare ? pointDelta(d.needs_review_pct, { goodWhenUp: false, unit: 'pt' }) : undefined,
      inverted: true,
      series: docs,
    },
    {
      label: '"Other" rate',
      value: `${c.other_rate_pct.toFixed(1)}%`,
      delta: compare ? pointDelta(d.other_rate_pct, { goodWhenUp: false, unit: 'pt' }) : undefined,
      inverted: true,
      series: docs,
    },
    {
      label: 'Avg confidence',
      value: c.avg_confidence.toFixed(2),
      delta: compare ? pointDelta(d.avg_confidence, { goodWhenUp: true, unit: '', decimals: 2 }) : undefined,
      inverted: false,
      series: docs,
    },
  ];
}

function KpiRow({
  summary,
  timeseries,
}: {
  summary: AnalyticsSummary;
  timeseries: DashboardData['timeseries']['points'];
}): React.JSX.Element {
  const docs = timeseries.map((p) => p.documents);
  const pages = timeseries.map((p) => p.pages);
  const tiles = buildTiles(summary, docs, pages);
  return (
    <div className="ds-dash__kpis">
      {tiles.map((tile) => (
        <KpiTile
          key={tile.label}
          label={tile.label}
          value={tile.value}
          delta={tile.delta}
          sparkline={
            tile.series.length > 0 ? (
              <Sparkline days={tile.series} inverted={tile.inverted} label={`${tile.label} trend`} />
            ) : undefined
          }
        />
      ))}
    </div>
  );
}

// --------------------------------------------------------------------- charts

/** The chart-type dropdown in Volume over time's header. */
const VOLUME_CHART_TYPE_OPTIONS: readonly SelectOption[] = [
  { value: 'area', label: 'Area' },
  { value: 'line', label: 'Line' },
  { value: 'step', label: 'Step' },
  { value: 'bar', label: 'Bar' },
  { value: 'scatter', label: 'Scatter' },
];

function VolumeCard({
  points,
  onAsk,
  onExport,
}: {
  points: DashboardData['timeseries']['points'];
  onAsk: () => void;
  onExport: () => void;
}): React.JSX.Element {
  const [chartType, setChartType] = useState<VolumeChartType>('area');
  const total = points.reduce((sum, p) => sum + p.documents, 0);
  const peak = points.reduce((max, p) => Math.max(max, p.documents), 0);
  const summary = `${fmtBig(total)} total · peak ${fmtBig(peak)}`;
  return (
    <ChartCard
      title="Volume over time"
      help="Documents ingested per day, from daily rollups."
      meta={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{summary}</span>
          <Select
            aria-label="Volume chart type"
            options={VOLUME_CHART_TYPE_OPTIONS}
            value={chartType}
            onChange={(event) => {
              setChartType(event.currentTarget.value as VolumeChartType);
            }}
            style={{ width: 'auto', padding: '3px 22px 3px 8px', fontSize: 11.5, borderRadius: 6 }}
          />
        </span>
      }
      empty={points.length === 0 ? 'No documents ingested in this period.' : undefined}
      onAsk={onAsk}
      onExport={onExport}
    >
      <LineArea
        summary={`Volume over time. ${summary}.`}
        chartType={chartType}
        points={points.map((p) => ({
          label: periodLabel(p.period),
          longLabel: periodLongLabel(p.period),
          value: p.documents,
        }))}
      />
    </ChartCard>
  );
}

/** Categories beyond this rank are folded into one "Others" row. */
/** Named types shown on the card; a sixth row aggregates the rest as "Others". */
const TYPE_MIX_TOP_N = 5;

function TypeMixCard({
  breakdown,
  onDrill,
  onViewAll,
  onAsk,
}: {
  breakdown: AnalyticsBreakdown;
  onDrill: (key: string) => void;
  onViewAll: () => void;
  onAsk: () => void;
}): React.JSX.Element {
  const empty = breakdown.buckets.length === 0;
  const [othersOpen, setOthersOpen] = useState(false);

  // Defensive, not authoritative ordering: shown top-to-bottom by count either
  // way, this just guarantees "top N" means the N largest even if the API ever
  // answers in a different order.
  const sorted = [...breakdown.buckets].sort((a, b) => b.documents - a.documents);
  const top = sorted.slice(0, TYPE_MIX_TOP_N);
  const rest = sorted.slice(TYPE_MIX_TOP_N);
  const othersTotal = rest.reduce((sum, b) => sum + b.documents, 0);

  const rows: HBarDatum[] = top.map((b) => ({
    label: b.label,
    value: b.documents,
    color: categoryColor(b.label),
    onSelect: () => {
      onDrill(b.key);
    },
  }));
  if (rest.length > 0) {
    rows.push({
      label: 'Others',
      value: othersTotal,
      // A fixed, deliberately muted token — not `categoryColor()`, which would
      // both permanently consume a real category's palette slot for a label
      // that isn't a genuine type, and visually suggest "Others" is one more
      // category rather than an aggregate of the rest.
      color: 'var(--t3)',
      // Not a peer of the rows above it: with 41 types the remainder outweighs
      // every named type put together, so letting it set the scale rendered the
      // real ones as stubs and hid the differences between them.
      aggregate: true,
      onSelect: () => {
        setOthersOpen(true);
      },
    });
  }

  return (
    <ChartCard
      title="Document type mix"
      meta={<span style={{ fontSize: 11, color: 'var(--t3)' }}>click to drill through</span>}
      empty={empty ? 'No classified documents in this period.' : undefined}
      onAsk={onAsk}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
        <HorizontalBar summary="Document type mix" valueHeader="Documents" data={rows} />
        {empty ? null : (
          <button
            type="button"
            className="ds-card__footerlink"
            onClick={() => {
              setOthersOpen(true);
            }}
          >
            View full breakdown →
          </button>
        )}
      </div>
      <TypeMixModal
        open={othersOpen}
        onClose={() => {
          setOthersOpen(false);
        }}
        buckets={sorted}
        onDrill={(key) => {
          setOthersOpen(false);
          onDrill(key);
        }}
        onViewAll={() => {
          setOthersOpen(false);
          onViewAll();
        }}
      />
    </ChartCard>
  );
}

function TypeMixModal({
  open,
  onClose,
  buckets,
  onDrill,
  onViewAll,
}: {
  open: boolean;
  onClose: () => void;
  buckets: AnalyticsBreakdown['buckets'];
  onDrill: (key: string) => void;
  onViewAll: () => void;
}): React.JSX.Element {
  // A rollup count can go negative under a known, separately-tracked backend
  // bug (a reclassification's reverse decrement racing/under-counting) —
  // clamped here only for this pie's own arithmetic (a negative slice has no
  // valid geometry), not silently "fixed": the raw bucket value is untouched
  // everywhere else this data is read.
  const colored = buckets.map((b) => ({ ...b, color: categoryColor(b.label), shown: Math.max(0, b.documents) }));
  const total = colored.reduce((sum, b) => sum + b.shown, 0);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Document type mix"
      width="chart"
      dismissible
      footer={
        <div className="ds-modal__footer">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={onViewAll}>
            View all in Documents →
          </Button>
        </div>
      }
    >
      <div className="ds-modal__body ds-modal__body--fill">
        <p style={{ fontSize: 12, color: 'var(--t3)', margin: '0 0 12px' }}>
          {fmtBig(total)} documents across {colored.length} {colored.length === 1 ? 'type' : 'types'}.
        </p>
        <div className="ds-donut ds-donut--scroll">
          <Pie
            summary="Document type mix"
            // The figure is the subject of this dialog, so it gets the room. CSS
            // caps it at the container width, so a narrow window shrinks it.
            size={440}
            data={colored.map((b) => ({
              label: b.label,
              value: b.shown,
              color: b.color,
              valueLabel: fmtBig(b.documents),
            }))}
          />
          <ul className="ds-donut__legend">
            {colored.map((b) => (
              <li key={b.key}>
                <button
                  type="button"
                  className="ds-donut__seg"
                  onClick={() => {
                    onDrill(b.key);
                  }}
                >
                  <span className="ds-donut__swatch" aria-hidden="true" style={{ background: b.color }} />
                  {b.label}
                  {/* The count alone. The share is what the wedge already shows,
                      and printing both invited reading "744 · 7%" as one figure. */}
                  <span className="ds-donut__seg-val">{fmtBig(b.documents)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Modal>
  );
}

function DimensionBarCard({
  title,
  dimension,
  breakdown,
  onDrill,
  onAsk,
}: {
  title: string;
  dimension: 'location' | 'scanner';
  breakdown: AnalyticsBreakdown;
  onDrill: (key: string) => void;
  onAsk: () => void;
}): React.JSX.Element {
  if (isUnmapped(breakdown)) {
    return (
      <ChartCard
        title={title}
        empty={
          <>
            No {dimension}s mapped yet. <Link to="/settings/locations">Name your {dimension}s</Link> to
            see this breakdown.
          </>
        }
        onAsk={onAsk}
      />
    );
  }
  if (breakdown.buckets.length === 0) {
    return <ChartCard title={title} empty="No documents in this period." onAsk={onAsk} />;
  }
  const byLabel = new Map(breakdown.buckets.map((b) => [b.label, b.key]));
  return (
    <ChartCard
      title={title}
      meta={<span style={{ fontSize: 11, color: 'var(--t3)' }}>click to drill</span>}
      onAsk={onAsk}
    >
      <Bar
        summary={`Documents by ${dimension}`}
        labelHeader={dimension === 'location' ? 'Location' : 'Scanner'}
        caption={`Documents by ${dimension}`}
        data={breakdown.buckets.map((b) => ({
          label: b.label,
          shortLabel: b.label.split(' ')[0] ?? b.label,
          value: b.documents,
        }))}
        onSelect={(datum) => {
          const key = byLabel.get(datum.label);
          if (key !== undefined) onDrill(key);
        }}
      />
    </ChartCard>
  );
}

function SourceDonutCard({
  breakdown,
  onDrill,
  onAsk,
}: {
  breakdown: AnalyticsBreakdown;
  onDrill: (key: string) => void;
  onAsk: () => void;
}): React.JSX.Element {
  const empty = breakdown.buckets.length === 0;
  const total = breakdown.buckets.reduce((sum, b) => sum + b.documents, 0);
  const colored = breakdown.buckets.map((b, i) => ({
    ...b,
    color: CATEGORICAL_TOKENS[i % CATEGORICAL_TOKENS.length] ?? 'var(--accent)',
  }));
  return (
    <ChartCard title="By source" empty={empty ? 'No documents in this period.' : undefined} onAsk={onAsk}>
      <div className="ds-donut">
        <Donut
          summary="Documents by source"
          total={total}
          data={colored.map((b) => ({
            label: b.label,
            value: b.documents,
            color: b.color,
            // The legend beside this shows shares only, so the count is what
            // hovering adds. Formatted here to match every other figure on the
            // dashboard rather than the donut's own centre-total shorthand.
            valueLabel: fmtBig(b.documents),
          }))}
        />
        <ul className="ds-donut__legend">
          {colored.map((b) => {
            const pct = total > 0 ? Math.round((b.documents / total) * 100) : 0;
            return (
              <li key={b.key}>
                <button
                  type="button"
                  className="ds-donut__seg"
                  onClick={() => {
                    onDrill(b.key);
                  }}
                >
                  <span className="ds-donut__swatch" aria-hidden="true" style={{ background: b.color }} />
                  {b.label}
                  <span className="ds-donut__seg-val">{pct}%</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </ChartCard>
  );
}

function ConfidenceCard({
  breakdown,
  onAsk,
}: {
  breakdown: AnalyticsBreakdown;
  onAsk: () => void;
}): React.JSX.Element {
  const empty = breakdown.buckets.length === 0;
  const bands = confidenceBands(
    breakdown.buckets.map((b) => ({ key: b.key, label: b.label, documents: b.documents })),
  );
  return (
    <ChartCard
      title="Confidence distribution"
      meta={<span style={{ fontSize: 11, color: 'var(--t3)' }}>threshold 0.75</span>}
      empty={empty ? 'No documents in this period.' : undefined}
      onAsk={onAsk}
    >
      <Histogram summary="Classification confidence distribution" bands={bands} threshold={0.75} />
    </ChartCard>
  );
}

// --------------------------------------------------------------- opportunities

function clusterTitle(o: Opportunity): string {
  const parts = [o.cluster.category_name];
  if (o.cluster.source) parts.push(o.cluster.source);
  return parts.join(' · ');
}

function Opportunities({
  items,
  onViewAll,
  onOpen,
}: {
  items: Opportunity[];
  onViewAll: () => void;
  onOpen: () => void;
}): React.JSX.Element {
  return (
    <>
      <div className="ds-dash__opps-head">
        <h2 className="ds-dash__opps-title">Top automation opportunities</h2>
        <button type="button" className="ds-linkbtn ds-linkbtn--right" onClick={onViewAll}>
          View all →
        </button>
      </div>
      {items.length === 0 ? (
        <p style={{ color: 'var(--t3)', fontSize: 12.5, margin: 0 }}>
          No automation opportunities scored yet — they appear once a cluster builds enough volume.
        </p>
      ) : (
        <div className="ds-dash__opps-grid">
          {items.slice(0, 3).map((o) => {
            const style = bandStyle(o.score);
            return (
              <button
                key={o.id}
                type="button"
                className="ds-opp"
                onClick={onOpen}
              >
                <div className="ds-opp__top">
                  <span
                    className="ds-opp__score"
                    style={{ background: style.bg, color: style.fg }}
                    aria-label={`Score ${String(o.score)}, ${style.label} band`}
                  >
                    {o.score}
                  </span>
                  <span className="ds-opp__cluster">{clusterTitle(o)}</span>
                </div>
                <span className="ds-opp__sig">
                  {fmtBig(o.signals.volume)} docs · {o.signals.avg_confidence.toFixed(2)} avg conf ·{' '}
                  {(o.signals.other_rate * 100).toFixed(1)}% Other
                </span>
                <span className="ds-opp__action">{o.recommended_action}</span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}